import { useToken } from "pages/Auth/hooks";
import { useMemo } from "react";
import { useLocation } from "react-router";
import useSWR, { SWRConfiguration } from "swr";
import { Permission, Resource } from "./constants";
import { IUser } from "./services/typings";
import userService from "./services/userService";

type UseCurrentUserOptions = SWRConfiguration<IUser> & { sellerId?: number };

export const useCurrentUser = (config: UseCurrentUserOptions = {}) => {
  const token = useToken();
  const { sellerId } = config;
  const { data, error, mutate } = useSWR(
    sellerId ? ["CURRENT_USER", sellerId] : "CURRENT_USER",
    () => userService.getCurrentUser({ sellerId }),
    {
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      revalidateOnFocus: !!token ? true : false,
      revalidateOnReconnect: true,
      dedupingInterval: 5000,
      ...config,
    }
  );

  const loading = data === undefined && !error;
  return [data, loading, error, mutate] as const;
};

export const usePermissions = (sellerId?: number | undefined) => {
  const [user] = useCurrentUser({ sellerId });
  return user?.role?.permissions || {};
};

export const useIsAdmin = () => {
  const [user] = useCurrentUser({ revalidateOnMount: false });
  return user?.role_id === "admin" || user?.role?.admin;
};

export const useUserType = () => {
  const [user] = useCurrentUser({ revalidateOnMount: false });
  return user?.type;
};

export const useAuthorize = (
  items: [Resource, Permission][],
  sellerId?: number | undefined
) => {
  const permissions = usePermissions(sellerId);
  const isAdmin = useIsAdmin();

  const isAuthorize = useMemo(
    () =>
      items?.some(
        ([resource, permission]) => permissions[`${resource}:${permission}`]
      ),
    [permissions, items]
  );

  if (!items || isAdmin) return true;
  return isAuthorize;
};
